import React, { cloneElement, isValidElement, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDataModel } from '@thd-nucleus/data-sources';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withLayoutGroup } from '@thd-olt-component-react/layout-manager';
import { ComponentPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Row } from '@thd-olt-component-react/grid';
import { Heading } from '@thd-olt-component-react/heading';
import { dataModel } from '../dataModel';
import { SpecificationGroupContent } from './SpecificationGroupContent';
import './specifications.scss';

export const SpecificationsDesktopComponent = (props) => {
  const {
    itemId,
    children
  } = props;
  const { channel } = useContext(ExperienceContext);

  const { data, loading, error } = useDataModel('product', { variables: { itemId } });

  if (loading) {
    return (
      <ComponentPlaceholder
        height={500}
        rows={10}
        color="#E0E0E0"
        showLoadingAnimation
      />
    );
  }

  if (error && !data) {
    return null;
  }

  if (!data?.product?.specificationGroup) {
    return null;
  }

  const { specificationGroup } = data.product;

  if (!specificationGroup.length) {
    return null;
  }

  return (
    <Row
      className="specifications"
      id="specifications"
      style={{ pointerEvents: 'auto' }}
    >
      <Row>
        <Heading title="Specifications" underline />
        {children && cloneElement(children, {
          itemId,
          maxfeatures: 8,
          minfeatures: 3,
          channel,
          displaySimilarLinks: true
        })}
        <SpecificationGroupContent specificationGroup={specificationGroup} />
      </Row>
    </Row>
  );
};

SpecificationsDesktopComponent.displayName = 'SpecificationsDesktop';

const propTypes = {
  /** SKU Number */
  itemId: PropTypes.string.isRequired,
  /** Can take an (optional) single child component of type KeyProductFeatures */
  children: (props, propName, componentName) => {
    const prop = props[propName];
    let error;
    if (prop && (!isValidElement(prop) || prop.type.name !== 'KeyProductFeatures')) {
      error = new Error(`'${componentName}' only accepts a single child of type 'KeyProductFeatures'`);
    }
    return error;
  }
};
SpecificationsDesktopComponent.propTypes = propTypes;

const defaultProps = {
  children: null
};
SpecificationsDesktopComponent.defaultProps = defaultProps;
SpecificationsDesktopComponent.dataModel = dataModel;

const HydratedComponent = withHydrator({
  id: 'specifications-desktop',
  scrollBuffer: 750,
  delay: 1500,
  preserveCtxVal: 'clientStore',
  placeholder: (
    <ComponentPlaceholder
      height={500}
      rows={10}
      color="#E0E0E0"
      showLoadingAnimation
    />
  )
}, SpecificationsDesktopComponent);
const DynamicComponent = withDynamicComponent(HydratedComponent);

export const SpecificationsDesktop = withLayoutGroup({
  name: 'specifications'
}, DynamicComponent);

SpecificationsDesktop.dataModel = dataModel;
SpecificationsDesktop.propTypes = propTypes;
SpecificationsDesktop.defaultProps = defaultProps;
